import { useEffect, useState } from "react";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface UserPreferences {
  chat_collapse_enabled: boolean;
  chat_collapsed_height: number;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  chat_collapse_enabled: true,
  chat_collapsed_height: 400,
};

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchPreferences();
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error) {
        // If no preferences exist, create default ones
        if (error.code === "PGRST116") {
          await createDefaultPreferences();
        } else {
          throw error;
        }
      } else if (data) {
        setPreferences({
          chat_collapse_enabled: data.chat_collapse_enabled,
          chat_collapsed_height: data.chat_collapsed_height,
        });
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPreferences = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .insert({
          user_id: user.id,
          ...DEFAULT_PREFERENCES,
        });

      if (error) throw error;
      setPreferences(DEFAULT_PREFERENCES);
    } catch (error) {
      console.error("Error creating preferences:", error);
    }
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user) return;

    try {
      const newPreferences = { ...preferences, ...updates };

      const { error } = await supabase
        .from("user_preferences")
        .upsert({
          user_id: user.id,
          ...newPreferences,
        });

      if (error) throw error;

      setPreferences(newPreferences);
      toast.success("Preferences updated");
    } catch (error) {
      console.error("Error updating preferences:", error);
      toast.error("Failed to update preferences");
    }
  };

  return {
    preferences,
    loading,
    updatePreferences,
  };
};
